/**
 * EscheatmentService — Missouri State Treasurer Unclaimed Property handoff
 * for drilled / deceased SDB contents.
 *
 * Port of legacy SPFx `SdbManagementScreen.loadEscheatment` (SPFx
 * src/webparts/accountOpeningV2/components/screens/SdbManagementScreen.tsx:290-316)
 * with two critical divergences mandated by the v3 brief:
 *
 *   1. NO HARDCODED ABANDONMENT YEARS. Legacy SPFx hardcoded `5` years
 *      for drilled + `1.5` years for deceased. Those values MUST come
 *      from the `/v3/config/escheatment-rules` seed. If the config has
 *      not yet been seeded the service returns `null` for the deadline
 *      and the UI renders "—" (never a guessed year).
 *
 *   2. MO-SPECIFIC by design. RSMo 447 governs unclaimed property for
 *      Missouri-chartered state banks. Other states would carry
 *      different dormancy windows and different state-treasurer report
 *      cycles; those variations live in the config, not in this code.
 *
 * The legacy pipeline wrote drilled contents to the `ao_SdbEscheatment`
 * SharePoint list. V3 replaces that with two calls:
 *   - `apiClient.retail.events.write({ eventType: 'sdb-escheatment-queued', ... })`
 *     for the audit trail.
 *   - `apiClient.retail.tasks.create({ type: 'sdb-escheatment', ... })`
 *     for RCC to act on in its queue.
 *
 * State handoff (actual NAUPA report submission to the MO State Treasurer)
 * is a back-office RCC function, not a Teams-app action. The Treasurer
 * record-of-contents form is rendered via RSC and delivered by RNC; this
 * service's job is only to queue the handoff.
 */
import type { ApiClient } from '@retail-ops/api-client';
import { asIso8601 } from '@retail-ops/shared-types';

/**
 * Config row surfaced by `/v3/config/escheatment-rules`. Until the v3.0
 * seed lands, the endpoint 503s; callers must therefore treat every
 * field as nullable.
 *
 * Shape is a superset of Missouri-specific fields — the same table
 * handles other states via `jurisdictionState` / `propertyType`.
 */
export interface EscheatmentRule {
  /** USPS state code (e.g. `MO`). */
  jurisdictionState: string;
  /**
   * One of `sdb-drilled`, `sdb-deceased`, `deposit-dormant`,
   * `loan-credit-balance`, etc. The service filters by this code.
   */
  propertyType: string;
  /** Years of dormancy / holding before state handoff. */
  abandonmentYears: number;
  /**
   * MM-DD of the annual state report deadline
   * (e.g. `11-01` for Missouri's November 1 cycle).
   */
  reportDeadlineMonthDay: string | null;
  /** Minimum account value triggering due-diligence contact. */
  dueDiligenceMinValue: number | null;
  /** Regulatory citation for officer reference (e.g. `RSMo 447.539.9`). */
  citation: string | null;
  /** ISO date the rule becomes effective. */
  effectiveFrom: string | null;
  /** ISO date the rule expires (null = open-ended). */
  effectiveUntil: string | null;
}

export type EscheatType = 'drilled' | 'deceased';

function escheatPropertyType(t: EscheatType): string {
  return t === 'deceased' ? 'sdb-deceased' : 'sdb-drilled';
}

/**
 * Request payload for handing off a drilled or deceased-renter box to
 * the Missouri Treasurer escheatment queue. Every dollar amount and
 * every date is optional — officers may not know everything at handoff
 * time (contents inventory may still be in motion), and the RCC team
 * can fill gaps later.
 */
export interface EscheatmentHandoffRequest {
  acctId: string;
  boxNumber: string | null;
  branchId: string | null;
  escheatType: EscheatType;
  /** Primary customer / renter CIF. */
  primaryCifId: string | null;
  primaryCustomerName: string | null;
  /** ISO date of the triggering event (drilling, DOD, last contact). */
  triggerEventDate: string;
  /** ISO date last known contact attempt was made. */
  lastContactDate?: string | null;
  /** Free-text inventory summary (drilled path uses Record of Contents PDF). */
  contentsDescription: string | null;
  /** Dollar value of contents if known. */
  estimatedValue: number | null;
  /** True if the customer or their last-known estate was sent due-diligence mail. */
  dueDiligenceSent: boolean;
  /** Officer who authorized handoff (UPN). */
  authorizedBy: string;
}

export interface EscheatmentHandoffResult {
  /** The abandonment period in years sourced from config. Null if unseeded. */
  abandonmentYears: number | null;
  /** ISO date by which the state handoff must occur. Null if abandonmentYears is null. */
  reportDueDate: string | null;
  /** Days remaining until the state report deadline. Null if reportDueDate is null. */
  daysUntilReport: number | null;
  /** The task id created for RCC follow-up (when present). */
  taskId: string | null;
  /** The event id for the handoff audit trail. */
  eventId: string | null;
  /** The rule row the service used, if any. */
  rule: EscheatmentRule | null;
}

function addYears(isoDate: string, years: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  const wholeYears = Math.floor(years);
  const fracMonths = Math.round((years - wholeYears) * 12);
  d.setFullYear(d.getFullYear() + wholeYears);
  d.setMonth(d.getMonth() + fracMonths);
  return d.toISOString().slice(0, 10);
}

function nextDeadline(reportMonthDay: string, afterIsoDate: string): string {
  const [mm, dd] = reportMonthDay.split('-').map((s) => Number.parseInt(s, 10));
  const after = new Date(`${afterIsoDate}T00:00:00`);
  const candidate = new Date(after.getFullYear(), (mm || 1) - 1, dd || 1);
  if (candidate.getTime() < after.getTime()) {
    candidate.setFullYear(candidate.getFullYear() + 1);
  }
  return candidate.toISOString().slice(0, 10);
}

function daysUntil(isoDate: string): number {
  const target = new Date(`${isoDate}T00:00:00`).getTime();
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00').getTime();
  return Math.round((target - today) / 86_400_000);
}

export class EscheatmentService {
  public constructor(private readonly client: ApiClient) {}

  /**
   * Load the escheatment rule for a given `(state, propertyType)` tuple.
   * Hits `/v3/config/escheatment-rules` via the raw HTTP surface — the
   * route isn't in `ConfigApi` yet because the Function endpoint is
   * still pending the v3.0 SQL seed.
   *
   * Returns `null` if the endpoint 503s or no matching row is seeded.
   * Callers MUST NOT substitute a hardcoded default when null is returned.
   */
  public async loadRule(
    state: string,
    propertyType: string,
    opts?: { signal?: AbortSignal },
  ): Promise<EscheatmentRule | null> {
    try {
      // Tunneled through the raw HTTP surface since ConfigApi hasn't
      // added a typed wrapper yet (pending Wave 3 config seed).
      const anyClient = this.client as unknown as {
        http: { get<T>(path: string, opts?: { signal?: AbortSignal; query?: Record<string, string> }): Promise<T> };
      };
      const resp = await anyClient.http.get<{ success: boolean; data?: ReadonlyArray<EscheatmentRule> }>(
        '/v3/config/escheatment-rules',
        { ...(opts?.signal ? { signal: opts.signal } : {}), query: { state, propertyType } },
      );
      const rows = resp?.data ?? [];
      const match = rows.find(
        (r) => r.jurisdictionState === state && r.propertyType === propertyType,
      );
      return match ?? null;
    } catch {
      // 503 or not-yet-seeded: return null and let the UI render "—".
      return null;
    }
  }

  /**
   * Queue an escheatment handoff for RCC. Writes an audit event and
   * creates a task; returns the computed (config-driven) deadline plus
   * the task + event ids. Does NOT submit to the state treasurer — that
   * is an RCC-queue activity handled by `RCC/EscheatmentQueue`.
   */
  public async queueHandoff(
    req: EscheatmentHandoffRequest,
    opts?: { signal?: AbortSignal },
  ): Promise<EscheatmentHandoffResult> {
    const propertyType = escheatPropertyType(req.escheatType);
    const rule = await this.loadRule('MO', propertyType, opts);

    // Deadline computation is config-driven — null if config hasn't been seeded.
    const abandonmentYears = rule?.abandonmentYears ?? null;
    const reportDueDate =
      abandonmentYears != null && rule?.reportDeadlineMonthDay
        ? nextDeadline(rule.reportDeadlineMonthDay, addYears(req.triggerEventDate, abandonmentYears))
        : null;
    const daysUntilReport = reportDueDate ? daysUntil(reportDueDate) : null;

    // --- Audit event ---
    let eventId: string | null = null;
    try {
      const res = await this.client.retail.events.write(
        {
          eventType: 'sdb-escheatment-queued',
          eventCategory: 'compliance',
          severity: 'warn',
          actor: req.authorizedBy,
          payloadJson: JSON.stringify({
            acctId: req.acctId,
            boxNumber: req.boxNumber,
            branchId: req.branchId,
            escheatType: req.escheatType,
            primaryCifId: req.primaryCifId,
            primaryCustomerName: req.primaryCustomerName,
            triggerEventDate: req.triggerEventDate,
            lastContactDate: req.lastContactDate ?? null,
            contentsDescription: req.contentsDescription,
            estimatedValue: req.estimatedValue,
            dueDiligenceSent: req.dueDiligenceSent,
            ruleApplied: rule
              ? {
                  state: rule.jurisdictionState,
                  propertyType: rule.propertyType,
                  abandonmentYears: rule.abandonmentYears,
                  citation: rule.citation,
                }
              : null,
            reportDueDate,
          }),
          timestamp: asIso8601(new Date().toISOString()),
        },
        opts?.signal ? { signal: opts.signal } : undefined,
      );
      eventId = res.ids[0] ?? null;
    } catch {
      eventId = null;
    }

    // --- RCC task ---
    let taskId: string | null = null;
    try {
      const res = await this.client.retail.tasks.create(
        {
          title: `RCC escheatment · SDB ${req.acctId}${req.boxNumber ? ` (Box ${req.boxNumber})` : ''}`,
          type: 'sdb-escheatment',
          category: 'compliance',
          priority: 'High',
          assignedTo: req.authorizedBy,
          description:
            `${req.escheatType === 'deceased' ? 'Deceased-renter' : 'Drilled'} SDB ` +
            `contents require MO Treasurer NAUPA submission. ` +
            (rule?.citation ? `Rule: ${rule.citation}. ` : '') +
            (reportDueDate ? `Report due ${reportDueDate}.` : 'Report deadline pending config seed.'),
          severity: 'warn',
          autoCreated: true,
          ...(reportDueDate ? { dueDate: asIso8601(`${reportDueDate}T00:00:00Z`) } : {}),
        },
        opts?.signal ? { signal: opts.signal } : undefined,
      );
      taskId = res.task?.taskId ?? null;
    } catch {
      taskId = null;
    }

    return {
      abandonmentYears,
      reportDueDate,
      daysUntilReport,
      taskId,
      eventId,
      rule,
    };
  }
}

/** Factory for callers that only have the ApiClient. */
export function createEscheatmentService(client: ApiClient): EscheatmentService {
  return new EscheatmentService(client);
}
